import { EFinancingProgram } from '@if/core/types'
import { Button, Grid, Paper, Stack, Typography } from '@mui/material'
import { Breadcrumb, PageError, PageSpinner } from '@src/components'
import { useCreditApplicationData } from '@src/contexts/CreditApplicationContext'
import { useBusinessErrors } from '@src/data/selectors/app-config-selectors'
import { EasyCareWorksheet } from '@src/data/types/EasyCareWorksheetSchema'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

const EasyCareWorksheetViewPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { creditApplication, worksheet: contextWorksheet, isLoading } = useCreditApplicationData()
  const worksheet = contextWorksheet as EasyCareWorksheet | undefined
  const error = useBusinessErrors()

  const breadCrumbs = useMemo(
    () => [
      { path: '/', label: t('breadcrumbs.home') },
      { path: '/Applications/browse', label: t('breadcrumbs.creditApplication') },
      {
        path: `/Applications/${EFinancingProgram.EasyCareProtectionPlan}/${creditApplication?.id}/view`,
        label: t('breadcrumbs.application').concat(` #${creditApplication?.referenceNumber}`),
      },
      { path: '#', label: t('worksheet.financing') },
    ],
    [creditApplication, t],
  )

  const rows = useMemo<{ label: string; value: string | number }[]>(
    () =>
      worksheet
        ? [
            { label: t('worksheetCommon.amountRequested'), value: worksheet.amountRequested },
            { label: t('worksheet.term'), value: worksheet.term },
            {
              label: t('worksheet.paymentFrequency'),
              value: t(`EPaymentFrequency.${worksheet.paymentFrequency}`, { ns: 'enum' }),
            },
            { label: t('worksheetCommon.activationDate'), value: worksheet.deliveryOn },
            { label: t('worksheet.firstPaymentOn'), value: worksheet.firstPaymentOn },
            { label: t('worksheetCommon.paymentAmount'), value: worksheet.paymentAmountForFrequency },
            { label: t('worksheet.totalAmountFinanced'), value: worksheet.totalAmountFinanced },
          ]
        : [],
    [t, worksheet],
  )

  return (
    <div>
      <PageSpinner isLoading={isLoading} />
      {!isLoading && creditApplication && worksheet && (
        <Stack spacing={3}>
          <PageError errors={error} />
          <Stack
            direction={{ sm: 'row', xs: 'column' }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ sm: 'self-end', xs: 'self-start' }}
          >
            <Breadcrumb trees={breadCrumbs} />
            <Button
              fullWidth={false}
              variant="contained"
              onClick={() =>
                navigate(`/Applications/${EFinancingProgram.EasyCareProtectionPlan}/${creditApplication.id}/worksheet/edit`)
              }
            >
              {t('common.edit')}
            </Button>
          </Stack>
          <Paper sx={{ p: 3 }}>
            <Grid container spacing={2}>
              {rows.map((row) => (
                <Grid key={row.label} size={{ xs: 12, sm: 6, md: 4 }}>
                  <Typography variant="caption" color="text.secondary">
                    {row.label}
                  </Typography>
                  <Typography variant="body1">{row.value}</Typography>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Stack>
      )}
    </div>
  )
}

export default EasyCareWorksheetViewPage
